import { createAsyncThunk } from '@reduxjs/toolkit';

import ooeConstants from '../constants';
import { selectDestination } from '../order/selectors';
import type { State } from '../reducers';
import { getVariation } from '../services/launchDarkly';
import { rexCateringClient } from '../shared/clients';
import { selectLocationNumber } from '../user/selectors';
import {
  addToFavoriteOrders,
  findCustomersByEmailAddress,
  getCustomerProfilesByPhoneNumber,
  getFavoriteCateringOrders,
  getGuestCateringOrderHistory,
  getPaymentAccount,
  removeFromFavoriteOrders,
  searchForCustomerProfile,
  searchForPaymentAccounts,
  updateFavoriteOrderName,
} from './apis';
import {
  selectCmtCustomerCFAId,
  selectCmtCustomerIsRegisteredMember,
  selectEmail,
  selectFirstName,
  selectLastName,
  selectMemberSearchValue,
  selectPhoneFormForApi,
  selectValidEmail,
  selectValidPhone,
} from './selectors';
import { rexMemberToSearchResult } from './transforms';
import {
  mapEmailSearchResultsToGuests,
  mapNameSearchResultsToGuests,
  mapOrdersToPastDeliveryAddresses,
  mapOrdersToPastOrders,
  mapPhoneSearchResultsToGuests,
} from './utils';

export const getMemberSearchPageNumber = (arg?: SearchForMembersArg) =>
  arg?.pageNumber ?? ooeConstants.FIRST_MEMBER_SEARCH_PAGE;

export const searchGuestsByName = createAsyncThunk('guest/searchByName', async (_, thunkAPI) => {
  const { getState } = thunkAPI;
  const state = getState() as State;
  const locationNumber = selectLocationNumber(state);
  const firstName = selectFirstName(state) ?? '';
  const lastName = selectLastName(state) ?? '';

  if (firstName.length === 0 && lastName.length === 0) {
    return { searchResults: [] };
  }

  const customers = await searchForCustomerProfile({
    locationNumber,
    firstName,
    lastName,
    cfaOneOnly: true,
  });
  return { searchResults: mapNameSearchResultsToGuests(customers) };
});

export const searchGuestsByEmail = createAsyncThunk('guest/searchByEmail', async (_, thunkAPI) => {
  const { getState } = thunkAPI;
  const state = getState() as State;

  const isValidEmail = selectValidEmail(state);
  if (!isValidEmail) {
    return { searchResults: [] };
  }

  const emailAddress = selectEmail(state) as string;
  const { customers = [] } = await findCustomersByEmailAddress({ emailAddress });
  return { searchResults: mapEmailSearchResultsToGuests(customers) };
});

export const searchGuestByPhone = createAsyncThunk('guest/searchByPhone', async (_, thunkAPI) => {
  const { getState } = thunkAPI;
  const state = getState() as State;

  const isValidPhone = selectValidPhone(state);
  if (!isValidPhone) {
    return { searchResults: [] };
  }

  const phoneNumber = selectPhoneFormForApi(state) as string;
  const { userProfiles = [] } = await getCustomerProfilesByPhoneNumber({ phoneNumber });
  return { searchResults: mapPhoneSearchResultsToGuests(userProfiles) };
});

export const searchForMembers = createAsyncThunk(
  'guest/searchForMembers',
  async (
    arg: SearchForMembersArg | undefined,
    { getState, signal },
  ): Promise<{ searchResults: MemberSearchMember[]; nextPage: number }> => {
    const query = selectMemberSearchValue(getState() as State).trim();
    if (!query) {
      // Match Rex: nextPage 0 means there is nothing left to fetch.
      return { searchResults: [], nextPage: 0 };
    }

    const pageNumber = getMemberSearchPageNumber(arg);
    // Lets a superseded search (see listeners.ts) actually cancel the rex-catering call instead of
    // just having its response ignored once it returns.
    const { members, nextPage } = await rexCateringClient.searchMembers({ query, pageNumber }, { signal });
    return { searchResults: members.map((member) => rexMemberToSearchResult.parse(member)), nextPage };
  },
  {
    condition: (arg, { getState }) => {
      const pageNumber = getMemberSearchPageNumber(arg);
      if (pageNumber <= ooeConstants.FIRST_MEMBER_SEARCH_PAGE) {
        return true;
      }

      const state = getState() as State;
      if (state.guest.loading.memberSearch || state.guest.loading.memberSearchMore) {
        return false;
      }

      return state.guest.memberSearchNextPage === pageNumber;
    },
  },
);

export const getVaultedCards = createAsyncThunk('guest/getVaultedCards', async (_, { getState }) => {
  const cfaId = selectCmtCustomerCFAId(getState() as State);
  const isRegisteredMember = selectCmtCustomerIsRegisteredMember(getState() as State);
  const vaultedCards = await getPaymentAccount({ cfaId, isRegisteredMember });
  return { vaultedCards };
});

export const validateCardZipcode = createAsyncThunk(
  'guest/validateCardZipcode',
  async (payload: { zip: string; card: Card }, { getState }) => {
    const cfaId = selectCmtCustomerCFAId(getState() as State);
    const isRegisteredMember = selectCmtCustomerIsRegisteredMember(getState() as State);
    const cards = await searchForPaymentAccounts({ ...payload, cfaId, isRegisteredMember });
    return { cards };
  },
);

export const getPastOrders = createAsyncThunk(
  'guest/getPastOrders',
  async (payload: { numToLoad: number }, { getState }) => {
    const cfaId = selectCmtCustomerCFAId(getState() as State);
    const isRegisteredMember = selectCmtCustomerIsRegisteredMember(getState() as State);
    const orders = await getGuestCateringOrderHistory({ ...payload, cfaId, isRegisteredMember });
    const pastOrders = mapOrdersToPastOrders(orders);
    return { pastOrders };
  },
);

export const getPastDeliveryAddresses = createAsyncThunk(
  'guest/getPastDeliveryAddresses',
  async (payload: { numToLoad: number }, { getState }) => {
    const cfaId = selectCmtCustomerCFAId(getState() as State);
    const isRegisteredMember = selectCmtCustomerIsRegisteredMember(getState() as State);
    const orders = await getGuestCateringOrderHistory({ ...payload, cfaId, isRegisteredMember });
    const addresses = mapOrdersToPastDeliveryAddresses(orders, {
      includeCompanyName: getVariation('showDeliveryAddressSearchBar', false),
    });
    return { addresses };
  },
);

export const getFavoriteOrders = createAsyncThunk('guest/getFavoriteOrders', async (_, { getState }) => {
  const state = getState() as State;
  const locationNumber = selectLocationNumber(state);
  const destination = selectDestination(state);
  const cfaId = selectCmtCustomerCFAId(state);
  const isRegisteredMember = selectCmtCustomerIsRegisteredMember(state);
  const favoriteOrders = await getFavoriteCateringOrders({
    locationNumber,
    destination,
    cfaId,
    isRegisteredMember,
  });
  return { favoriteOrders };
});

export const addToFavorites = createAsyncThunk(
  'guest/addToFavorites',
  async (payload: { orderId: string; name: string }, { getState }) => {
    const cfaId = selectCmtCustomerCFAId(getState() as State);
    const isRegisteredMember = selectCmtCustomerIsRegisteredMember(getState() as State);
    await addToFavoriteOrders({ ...payload, cfaId, isRegisteredMember });
  },
);

export const removeFromFavorites = createAsyncThunk(
  'guest/removeFromFavorites',
  async (payload: { favoriteOrderId: string }, { getState }) => {
    const cfaId = selectCmtCustomerCFAId(getState() as State);
    const isRegisteredMember = selectCmtCustomerIsRegisteredMember(getState() as State);
    return await removeFromFavoriteOrders({ ...payload, cfaId, isRegisteredMember });
  },
);

export const updateFavoriteName = createAsyncThunk(
  'guest/updateFavoriteName',
  async (payload: { favoriteOrderId: string; favoriteName: string }, { getState }) => {
    const cfaId = selectCmtCustomerCFAId(getState() as State);
    const isRegisteredMember = selectCmtCustomerIsRegisteredMember(getState() as State);
    return await updateFavoriteOrderName({ ...payload, cfaId, isRegisteredMember });
  },
);

export const initializeGuestAccount = createAsyncThunk(
  'guest/initializeGuestAccount',
  async (): Promise<{ cfaId: string }> => {
    const { cfaId } = await rexCateringClient.initializeGuestAccount({});
    return { cfaId };
  },
);
